import React, { useState, useEffect, useCallback } from 'react';
import { Barcode, Trash2, Plus, Loader2 } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { itemUnitsService } from '../../../../../services/itemUnitsService';
import { ItemUnitsDto } from '../../../../../types/itemUnits';
import { useBarcodeRegistrationEnabled } from '../../../../../hooks/useBarcodeRegistrationEnabled';
import { ScanTextInput } from '../../../../common/inputs';

/**
 * Alternative strekkoder (ItemUnits) på en vare — vedlikehold i vareadmin (TW 36320099).
 * Lokalt registrerte koder overskrives ikke av eSENTRAL-vareimporten (i motsetning til
 * Items.ItemBarCode). Registrering/sletting gates av items.allowBarcodeRegistration.
 */
interface ItemBarcodesSectionProps {
  companyNo: string;
  itemNo: string;
  itemUnitCode?: string | null;
  /** Skjul egen overskrift når seksjonen ligger i en ramme som har sin egen (varekort v2). */
  hideHeading?: boolean;
}

export const ItemBarcodesSection: React.FC<ItemBarcodesSectionProps> = ({ companyNo, itemNo, itemUnitCode, hideHeading }) => {
  const { t } = useTranslation();
  const registrationEnabled = useBarcodeRegistrationEnabled();
  const [units, setUnits] = useState<ItemUnitsDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [newBarcode, setNewBarcode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const result = await itemUnitsService.getItemUnitsByCompanyAndItem(companyNo, itemNo);
      setUnits(result);
    } catch (err) {
      console.error('Error loading item units:', err);
      setUnits([]);
    } finally {
      setLoading(false);
    }
  }, [companyNo, itemNo]);

  useEffect(() => { void load(); }, [load]);

  const handleAdd = async () => {
    const barcode = newBarcode.trim();
    if (!barcode || saving) return;
    setSaving(true);
    setError(null);
    try {
      await itemUnitsService.createItemUnit({
        companyNo,
        itemNo,
        itemUnitCode: itemUnitCode || '1',
        itemUnitTxt: null,
        itemBarCode: barcode,
        unitFactor: 1
      });
      setNewBarcode('');
      await load();
    } catch (err) {
      // 409 fra backend bærer forklarende melding (duplikat på annen vare)
      setError(err instanceof Error ? err.message : t('common.errors.saveFailed', {}, 'Kunne ikke lagre'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (unit: ItemUnitsDto) => {
    if (!window.confirm(t('warehouse.modules.itemAdmin.barcodes.confirmDelete', { '0': unit.itemBarCode }, `Slette strekkoden ${unit.itemBarCode}?`))) {
      return;
    }
    try {
      await itemUnitsService.deleteItemUnit(companyNo, unit.itemBarCode);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.errors.deleteFailed', {}, 'Kunne ikke slette'));
    }
  };

  return (
    <div>
      {!hideHeading && (
        <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-1 flex items-center gap-2">
          <Barcode className="h-4 w-4 text-gray-500" />
          {t('warehouse.modules.itemAdmin.barcodes.title', {}, 'Alternative strekkoder')}
        </h3>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
        </div>
      ) : (
        <>
          {units.length === 0 ? (
            <p className="text-sm text-gray-500 mb-3">
              {t('warehouse.modules.itemAdmin.barcodes.none', {}, 'Ingen alternative strekkoder registrert.')}
            </p>
          ) : (
            <div className="space-y-1.5 mb-3">
              {units.map(unit => (
                <div key={unit.id ?? `${unit.itemBarCode}`} className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                  <div className="min-w-0">
                    <span className="font-mono text-sm text-gray-900 break-all">{unit.itemBarCode}</span>
                    {unit.createdBy && (
                      <span className="ml-2 text-xs text-gray-400">
                        {t('warehouse.modules.itemAdmin.barcodes.createdBy', { '0': unit.createdBy }, `registrert av ${unit.createdBy}`)}
                      </span>
                    )}
                  </div>
                  {registrationEnabled && (
                    <button
                      onClick={() => handleDelete(unit)}
                      className="flex-shrink-0 p-2 min-h-[40px] min-w-[40px] flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      aria-label={t('common.buttons.delete', {}, 'Slett')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {registrationEnabled && (
            <div className="flex items-center gap-2">
              <ScanTextInput
                value={newBarcode}
                onChange={(e) => setNewBarcode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void handleAdd(); }}
                placeholder={t('warehouse.modules.itemAdmin.barcodes.addPlaceholder', {}, 'Skann eller skriv ny strekkode...')}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
              />
              <button
                onClick={handleAdd}
                disabled={saving || !newBarcode.trim()}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {t('warehouse.modules.itemAdmin.barcodes.addButton', {}, 'Legg til')}
              </button>
            </div>
          )}

          {error && (
            <div className="mt-2 text-sm text-red-600 break-words">{error}</div>
          )}
        </>
      )}
    </div>
  );
};
